import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { FieldSurveyorOnly } from './FieldSurveyorOnly'
import { getFacilities, type FacilityOption } from '../../lib/services/facilities'
import { getAssignments, type Visit } from '../../lib/services/visits'
import {
  presentationStatusFromVisit,
  presentationStatusLabel,
  type PresentationStatus,
} from '../../lib/presentationStatus'

type SegValue = 'all' | 'active' | 'done'

function rank(s: PresentationStatus): number {
  return s === 'action' ? 0 : s === 'working' ? 1 : 2
}

export function FacilitiesListPage() {
  const navigate = useNavigate()
  const [seg, setSeg] = useState<SegValue>('all')

  const { data: facilities = [], isLoading, error } = useQuery({
    queryKey: ['facilities'],
    queryFn: getFacilities,
  })

  const { data: visits = [] } = useQuery({
    queryKey: ['assignments'],
    queryFn: getAssignments,
  })

  // Collapse each facility's most-urgent visit status into the 3-bucket tag.
  const statusByFacility = new Map<string, PresentationStatus>()
  for (const v of visits as Visit[]) {
    if (!v.facility_id) continue
    const existing = statusByFacility.get(v.facility_id)
    const bucket = presentationStatusFromVisit(v.status)
    if (!existing || rank(bucket) < rank(existing)) statusByFacility.set(v.facility_id, bucket)
  }

  const bySeg = (f: FacilityOption) => {
    if (seg === 'all') return true
    const s = statusByFacility.get(f.facility_id)
    if (seg === 'active') return s === 'action' || s === 'working'
    return s === 'done'
  }
  const filtered = facilities.filter(bySeg)
  const total = facilities.length
  const activeCount = facilities.filter((f) => {
    const s = statusByFacility.get(f.facility_id)
    return s === 'action' || s === 'working'
  }).length
  const doneCount = facilities.filter((f) => statusByFacility.get(f.facility_id) === 'done').length

  return (
    <FieldSurveyorOnly>
    <FieldAppShell eyebrow="Field surveyor" title="My facilities" backTo="/today">
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => navigate('/facilities/new')}
          className="fs-btn-primary"
        >
          <Plus size={15} />
          Add facility
        </button>

        <div className="fs-seg" role="tablist" aria-label="Facility filter">
          <button
            type="button"
            role="tab"
            aria-selected={seg === 'all'}
            className={seg === 'all' ? 'active' : ''}
            onClick={() => setSeg('all')}
          >
            All ({total})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={seg === 'active'}
            className={seg === 'active' ? 'active' : ''}
            onClick={() => setSeg('active')}
          >
            Active ({activeCount})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={seg === 'done'}
            className={seg === 'done' ? 'active' : ''}
            onClick={() => setSeg('done')}
          >
            Done ({doneCount})
          </button>
        </div>

        {isLoading && <p className="py-8 text-center text-sm text-slate-500">Loading facilities…</p>}
        {error && <p className="py-8 text-center text-sm text-red-600">{(error as Error).message}</p>}

        {!isLoading && !error && filtered.length > 0 && (
          <div className="fs-surface overflow-hidden">
            {filtered.map((facility) => {
              const bucket = statusByFacility.get(facility.facility_id)
              const location = [facility.city, facility.state].filter(Boolean).join(' ')
              const line = [facility.address, location].filter(Boolean).join(' · ')
              return (
                <button
                  key={facility.facility_id}
                  type="button"
                  onClick={() => navigate(`/facilities/${facility.facility_id}`)}
                  className="fs-row w-full"
                >
                  <div className="min-w-0 flex-1">
                    <p className="fs-row-title truncate">{facility.facility_name}</p>
                    {line && <p className="fs-row-meta mt-0.5 truncate">{line}</p>}
                  </div>
                  {bucket && (
                    <span className={`fs-tag fs-tag-${bucket}`}>
                      {presentationStatusLabel(bucket)}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="fs-surface px-4 py-10 text-center text-sm text-slate-500">
            No facilities yet.
          </div>
        )}

      </div>
    </FieldAppShell>
    </FieldSurveyorOnly>
  )
}
